/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import UsagePeriodsEditor from './UsagePeriodsEditor';
import ResourceAssignmentEditDialog from './ResourceAssignmentEditDialog';
import { formatUsagePeriod, toUsagePayload, validateUsageRows } from '../../utils/usagePeriods';

const formatRateBasis = (basis) => {
  if (!basis) return '/ unit';
  return `/ ${basis}`;
};

const ResourceAssignDialog = ({ open, onClose, project, application, onSave }) => {
  const projectData = project || application;

  const [facilityResources, setFacilityResources] = useState([]);
  const [currentAssignments, setCurrentAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Add-new-resource form (quantity as string so user can clear and type)
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newResource, setNewResource] = useState({ resource: '', quantity: '1', rate_id: '' });
  const [newUsageRows, setNewUsageRows] = useState([]);

  // Assignment open in the edit dialog (rate, quantity, usage dates)
  const [editingAssignment, setEditingAssignment] = useState(null);

  const facilityId = projectData?.facility?._id || projectData?.facility;

  const fetchData = async () => {
    if (!projectData?._id) return;
    try {
      setLoading(true);
      setError(null);

      const resourcesUrl = facilityId
        ? `/api/resources?facility=${facilityId}`
        : '/api/resources';
      const [resourcesRes, assignmentsRes] = await Promise.all([
        axios.get(resourcesUrl),
        axios.get(`/api/resources/assignments/project/${projectData._id}`)
      ]);

      setFacilityResources(resourcesRes.data || []);
      setCurrentAssignments(assignmentsRes.data?.assignments || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading resources:', err);
      setError('Failed to load resources. Please try again.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && projectData?._id) {
      fetchData();
      setIsAddingNew(false);
      setEditingAssignment(null);
      setNewResource({ resource: '', quantity: '1', rate_id: '' });
      setNewUsageRows([]);
    }
  }, [open, projectData?._id]);

  // All facility resources are always available in the dropdown
  // (the same resource can be assigned multiple times with different rates)
  const selectedNewResource = facilityResources.find(r => r._id === newResource.resource);
  const selectedUnit = selectedNewResource?.unit;
  const unitRates = selectedUnit?.rates || [];

  // ─── ADD ──────────────────────────────────────────────────
  const handleAddResource = async () => {
    const q = parseFloat(String(newResource.quantity).trim());
    if (!newResource.resource || isNaN(q) || q <= 0) {
      toast.error('Please select a resource and enter a valid quantity (greater than 0; decimals allowed)');
      return;
    }
    if (unitRates.length > 0 && !newResource.rate_id) {
      toast.error('Please select a rate for this resource');
      return;
    }
    const usageError = validateUsageRows(newUsageRows);
    if (usageError) {
      toast.error(usageError);
      return;
    }

    try {
      setSaving(true);
      await axios.post(`/api/resources/assignments/project/${projectData._id}`, {
        resource: newResource.resource,
        quantity: q,
        rate_id: newResource.rate_id,
        usage_periods: toUsagePayload(newUsageRows)
      });
      toast.success('Resource assigned successfully');
      setIsAddingNew(false);
      setNewResource({ resource: '', quantity: '1', rate_id: '' });
      setNewUsageRows([]);
      await fetchData();
      onSave?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to assign resource');
    } finally {
      setSaving(false);
    }
  };

  // ─── DELETE ───────────────────────────────────────────────
  const handleDeleteEntry = async (assignment) => {
    if (assignment.invoiced_quantity > 0) {
      toast.error('Cannot delete: this assignment has been invoiced');
      return;
    }
    try {
      setSaving(true);
      await axios.delete(
        `/api/resources/assignments/project/${projectData._id}/${assignment._id}`
      );
      toast.success('Assignment removed');
      await fetchData();
      onSave?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete assignment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Assign Resources to Project</Typography>
      </DialogTitle>

      <DialogContent sx={{ padding: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1">
                <strong>Project:</strong> {projectData?.project_title || projectData?.project_id || 'N/A'}
              </Typography>
              {facilityResources.length === 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No resources found for this facility. Please create resources in Resource Management first.
                </Alert>
              )}
            </Box>

            {/* ─── Add New Resource Form ─── */}
            {isAddingNew && (
              <Card
                sx={{
                  mb: 4,
                  border: '1px solid var(--brand-primary-soft, #f3d0d6)',
                  boxShadow: '0 6px 20px rgba(15, 23, 42, 0.14), 0 2px 6px rgba(15, 23, 42, 0.08)'
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>Add New Resource Assignment</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    You can assign the same resource multiple times with different rates.
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Resource</InputLabel>
                        <Select
                          value={newResource.resource}
                          onChange={(e) => setNewResource({ ...newResource, resource: e.target.value, rate_id: '' })}
                          label="Select Resource"
                        >
                          {facilityResources.map((r) => (
                            <MenuItem key={r._id} value={r._id}>
                              {r.resource} ({r.unit?.unit_short || r.unit?.base_unit || ''})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Rate</InputLabel>
                        <Select
                          value={newResource.rate_id}
                          onChange={(e) => setNewResource({ ...newResource, rate_id: e.target.value })}
                          label="Rate"
                          disabled={unitRates.length === 0}
                        >
                          {unitRates.map((r) => (
                            <MenuItem key={r.rate_id} value={r.rate_id}>
                              {r.display_name} - ${r.rate?.toFixed(2)} {formatRateBasis(r.rate_basis)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Quantity"
                        type="number"
                        fullWidth
                        size="small"
                        value={newResource.quantity ?? ''}
                        onChange={(e) => setNewResource({ ...newResource, quantity: e.target.value })}
                        inputProps={{ min: 0, step: 'any' }}
                        placeholder="e.g., 4.5"
                      />
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      mt: 2.5,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: 'var(--brand-primary-softer, #fdf3f5)',
                      border: '1px solid var(--brand-primary-soft, #f3d0d6)'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Usage dates (optional)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      For tracking when the resource was used. Billing is by quantity and does not use these dates.
                    </Typography>
                    <UsagePeriodsEditor rows={newUsageRows} onChange={setNewUsageRows} disabled={saving} />
                  </Box>
                  {newResource.resource && newResource.rate_id && (() => { const qty = parseFloat(String(newResource.quantity).trim()); return !isNaN(qty) && qty > 0; })() && (
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f0f7ff', borderRadius: 1 }}>
                      <Typography variant="body2">
                        <strong>Preview:</strong> {newResource.quantity} x{' '}
                        {selectedNewResource?.resource} @{' '}
                        {unitRates.find(r => r.rate_id === newResource.rate_id)?.display_name} (${unitRates.find(r => r.rate_id === newResource.rate_id)?.rate?.toFixed(2)} {formatRateBasis(unitRates.find(r => r.rate_id === newResource.rate_id)?.rate_basis)})
                        {' = '}
                        <strong>
                          ${(newResource.quantity * (unitRates.find(r => r.rate_id === newResource.rate_id)?.rate || 0)).toFixed(2)}
                        </strong>
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                    <Button variant="outlined" onClick={() => { setIsAddingNew(false); setNewUsageRows([]); }} disabled={saving}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleAddResource}
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                      disabled={saving}
                      sx={{ fontWeight: 700, px: 4 }}
                    >
                      Add Resource
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* ─── Assigned Resources Table ─── */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                Assigned Resources ({currentAssignments.length})
              </Typography>
              {currentAssignments.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="left">Resource</TableCell>
                        <TableCell align="left">Unit</TableCell>
                        <TableCell align="left">Rate</TableCell>
                        <TableCell align="center">Qty</TableCell>
                        <TableCell align="left">Usage Dates</TableCell>
                        <TableCell align="center">Invoiced</TableCell>
                        <TableCell align="center">Unbilled</TableCell>
                        <TableCell align="right">Est. Cost</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentAssignments.map((assignment) => {
                        const unit = assignment.resource?.unit;
                        const rates = unit?.rates || [];
                        const rateObj = rates.find(r => r.rate_id === assignment.rate_id);
                        const invoicedQty = assignment.invoiced_quantity || 0;
                        const displayRate = rateObj?.rate || assignment.rate || 0;
                        const unbilledQty = Math.max(0, assignment.quantity - invoicedQty);

                        return (
                          <TableRow key={assignment._id} sx={{ '& td': { verticalAlign: 'top', paddingTop: '12px' } }}>
                            <TableCell align="left">
                              <Typography variant="body2" fontWeight={500}>
                                {assignment.resource?.resource || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {assignment.resource?.description || ''}
                              </Typography>
                            </TableCell>
                            <TableCell align="left">{unit?.unit_short || unit?.base_unit || 'N/A'}</TableCell>
                            <TableCell align="left">
                              <Typography variant="body2">
                                {rateObj?.display_name || assignment.rate_id || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ${displayRate.toFixed(2)} {formatRateBasis(rateObj?.rate_basis)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">{assignment.quantity}</Typography>
                            </TableCell>
                            <TableCell align="left">
                              {(assignment.usage_periods || []).length > 0 ? (
                                assignment.usage_periods.map((p, i) => (
                                  <Typography key={p._id || i} variant="caption" component="div" sx={{ whiteSpace: 'nowrap' }}>
                                    {formatUsagePeriod(p)}{p.note ? ` · ${p.note}` : ''}
                                  </Typography>
                                ))
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {invoicedQty > 0 ? (
                                <Chip size="small" color="success" icon={<CheckCircleIcon />} label={invoicedQty} />
                              ) : (
                                <Typography variant="body2" color="text.secondary">0</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip size="small" color={unbilledQty > 0 ? 'warning' : 'default'} label={unbilledQty} />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                ${(unbilledQty * displayRate).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Tooltip title="Edit rate, quantity and usage dates">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => setEditingAssignment(assignment)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={invoicedQty > 0 ? 'Cannot delete (invoiced)' : 'Delete'}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteEntry(assignment)}
                                      disabled={invoicedQty > 0 || saving}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography color="textSecondary" align="center">
                    No resources assigned yet. Use the Available Resources section below to assign.
                  </Typography>
                </Paper>
              )}
            </Box>

            {/* ─── Available Resources (assign from here) ─── */}
            {facilityResources.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>Available Resources</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Click "Assign" to add a resource. The same resource can be assigned multiple times at different rates.
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Resource</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell>Available Rates</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {facilityResources.map((r) => (
                        <TableRow key={r._id}>
                          <TableCell>{r.resource}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {r.description}
                            </Typography>
                          </TableCell>
                          <TableCell>{r.unit?.unit_short || r.unit?.base_unit || 'N/A'}</TableCell>
                          <TableCell>
                            {r.unit?.rates?.map(rate => (
                              <Chip
                                key={rate.rate_id}
                                label={`${rate.display_name}: $${rate.rate?.toFixed(2)} ${formatRateBasis(rate.rate_basis)}`}
                                size="small"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </TableCell>
                          <TableCell>
                            <Button
                              startIcon={<AddIcon />}
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                const rates = r.unit?.rates || [];
                                setIsAddingNew(true);
                                setNewResource({
                                  resource: r._id,
                                  quantity: '1',
                                  rate_id: rates.length === 1 ? rates[0].rate_id : ''
                                });
                              }}
                            >
                              Assign
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <ResourceAssignmentEditDialog
        open={!!editingAssignment}
        onClose={() => setEditingAssignment(null)}
        projectId={projectData?._id}
        assignment={editingAssignment}
        onSaved={async () => { await fetchData(); onSave?.(); }}
      />
    </Dialog>
  );
};

export default ResourceAssignDialog;
